import { useEffect, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { NavMesh } from '../lib/navmesh'
import { buildStudioFloorNavmeshData, STUDIO_FLOOR_SIZE } from '../lib/flatNavmesh'
import { navmeshCandidates, collisionMeshCandidates, fetchFirstOk } from '../lib/navmeshGridKey'
import { movement } from '../lib/movementComponent'
import { avatarFloor } from '../lib/avatarFloor'
import { EventRuntime } from '../lib/EventRuntime'
import { navSettingsStore } from '../lib/navSettingsStore'
import '../lib/navDev' // exposes window.__NAV_DEV__ for backend-free testing

/**
 * NavMeshVolume — loads the baked navmesh for the current splat, wires the
 * movement component, and ticks it once per frame to walk the avatar along its
 * path (ENG-592). Renders a debug overlay (navmesh edges + active path) when
 * ?nav=1 is set. Returns the overlay group or null.
 *
 * Off the React hot path: navmesh + group refs only; useFrame calls
 * movement.tick(dt) which mutates window.__AVATAR_GROUP__.position directly.
 *
 * No silent fallback (CLAUDE.md): an environment whose bake is missing surfaces
 * as window.__NAVMESH__.state === 'no-cache' with a console pointer — never
 * fake. A scene with NO environment is a different case, not a missing bake: the
 * studio stage's own reference grid becomes the navmesh, reported as
 * state === 'studio-floor' so it can't be mistaken for a bake.
 */
export default function NavMeshVolume({ splatUrl, splatVersion, reloadKey = 0, selected = false }) {
  const scene = useThree(s => s.scene)
  const navRef = useRef(null)
  const collisionRef = useRef(null)
  const [overlay, setOverlay] = useState(null) // { positions, indices } for debug lines
  // Subscribe to the Build Navmesh panel's show-toggles so the checkboxes flip
  // the navmesh/collision layers live (capsule is owned by AgentCapsuleGizmo).
  const [, force] = useState(0)
  useEffect(() => navSettingsStore.subscribe(() => force(n => n + 1)), [])
  // Navigation gizmos (navmesh overlay, collision mesh) are authoring aids —
  // only shown while the Build → Build Navmesh panel is open, so the normal
  // live view isn't cluttered. The navmesh DATA still loads for movement.
  const { show, style } = navSettingsStore.getState()
  // Debug overlay (navmesh edges + active path) is ON by default so you can see
  // what's happening from the start. Disable explicitly with ?nav=0 or
  // localStorage.nav = '0'.
  const showDebug = typeof window === 'undefined'
    ? false
    : (new URLSearchParams(window.location.search).get('nav') !== '0' && window.localStorage?.nav !== '0')
  // Two ways to ask for the navmesh overlay: the outliner NAVMESH row's "Show
  // navmesh grid" toggle (persistent), or selecting that row (transient). ?nav=0
  // is a BOOT-TIME default and deliberately vetoes neither — an explicit click
  // now beats a URL flag set before the page loaded.
  const wantsOverlay = show.navmesh || selected

  useEffect(() => {
    let cancelled = false
    const setState = (state, extra) => {
      if (typeof window !== 'undefined') window.__NAVMESH__ = { state, ...extra }
    }
    // Wire movement to the live event runtime (fire onMoveCompleted).
    movement.setEventSink((type, data) => EventRuntime.fireEvent(type, data))
    movement.setGroup((typeof window !== 'undefined' && window.__AVATAR_GROUP__) || null)

    // No environment splat: there is nothing to fetch a bake for, but the scene
    // still HAS a floor — the studio stage and its reference grid — so the
    // walkable area is known rather than guessed. Build the grid as the navmesh
    // so navigation (and the right-click "Walk here" menu) works on a bare
    // character instead of needing __NAV_DEV__.loadFlat() first.
    //
    // Reported as its own state, never 'applied': LightingStatusChip reads that
    // to tell the user an ENVIRONMENT has a baked navmesh, and a synthetic floor
    // must not make that chip claim a bake nobody produced. An environment that
    // is missing its bake still reports 'no-cache' below — this fills the empty
    // scene only, and never stands in for a real environment's absent data.
    if (!splatUrl) {
      const floorY = (typeof window !== 'undefined' && window.__AVATAR_FLOOR__?.getY?.()) ?? 0
      const nm = new NavMesh(buildStudioFloorNavmeshData(floorY))
      navRef.current = nm
      movement.setNavMesh(nm)
      if (typeof window !== 'undefined') window.__NAVMESH_INSTANCE__ = nm
      if (showDebug) setOverlay(buildOverlay(nm))
      setState('studio-floor', { nodeCount: nm.nodeCount, size: STUDIO_FLOOR_SIZE, floorY })
      console.log(`[NavMeshVolume] no environment — using the studio floor grid as the navmesh (${nm.nodeCount} nodes, ${STUDIO_FLOOR_SIZE} m @ y=${floorY})`)
      return
    }
    setState('pending')

    ;(async () => {
      const bust = splatVersion ? `?v=${encodeURIComponent(splatVersion)}` : ''
      const candidates = await navmeshCandidates(splatUrl)
      if (cancelled) return
      const { url, res } = await fetchFirstOk(candidates, bust)
      // fetchFirstOk already filtered HTML/non-200; re-check the chosen result.
      const ct = res?.headers.get('content-type') || ''
      if (!res || !res.ok || /html/.test(ct)) {
        console.warn(`[NavMeshVolume] no baked navmesh (tried ${candidates.join(', ')}). The bake service writes baked/navmesh.json beside the .ply; or inject a test grid: __NAV_DEV__.loadFlat()`)
        setState('no-cache', { url })
        return
      }
      const data = await res.json()
      if (cancelled) return
      const nm = new NavMesh(data)
      navRef.current = nm
      movement.setNavMesh(nm)
      if (typeof window !== 'undefined') window.__NAVMESH_INSTANCE__ = nm
      if (showDebug) setOverlay(buildOverlay(nm))
      setState('applied', { url, nodeCount: nm.nodeCount })
      console.log(`[NavMeshVolume] applied navmesh from ${url} (${nm.nodeCount} nodes)`)
    })().catch(err => {
      if (cancelled) return
      console.error('[NavMeshVolume] failed:', err)
      setState('error', { error: String(err) })
    })

    return () => {
      cancelled = true
      navRef.current = null
      movement.setNavMesh(null)
      setOverlay(null)
      if (typeof window !== 'undefined') { delete window.__NAVMESH__; delete window.__NAVMESH_INSTANCE__ }
    }
    // reloadKey: re-fetch when a bake for this env completes (bakeReload.js), so a
    // freshly-built navmesh.json is loaded without a page reload.
  }, [splatUrl, splatVersion, showDebug, reloadKey])

  // Test/console seam: whether the navigation overlay is actually being drawn.
  // Mirrored from the same two conditions the render gate below uses, so a spec
  // can assert "selecting the row draws the graph" without reaching into the R3F
  // tree (the overlay is a sibling group, not part of __AVATAR_SCENE__).
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.__NAVMESH_OVERLAY_VISIBLE__ = !!overlay && wantsOverlay
  }, [overlay, wantsOverlay])

  // Selection or the toggle can ask for the overlay after the navmesh loaded —
  // and with ?nav=0 the load path skips building it — so build on demand rather
  // than leaving the request drawing nothing.
  useEffect(() => {
    if (!wantsOverlay || overlay || !navRef.current) return
    setOverlay(buildOverlay(navRef.current))
  }, [wantsOverlay, overlay])

  // Rebuild the debug overlay when a dev navmesh is injected (window.__NAV_DEV__).
  useEffect(() => {
    if (!showDebug) return
    const onUpdate = () => {
      const nm = (typeof window !== 'undefined' && window.__NAVMESH_INSTANCE__) || null
      navRef.current = nm
      setOverlay(nm ? buildOverlay(nm) : null)
    }
    window.addEventListener('aos-navmesh-updated', onUpdate)
    return () => window.removeEventListener('aos-navmesh-updated', onUpdate)
  }, [showDebug])

  // Load + render the collision mesh (splat-transform's carved .collision.glb).
  // It's in engine space; the constant Rz(180°) (group rotation z=π) brings it to
  // Spark world. Orange wireframe, always-on-top, on by default with the overlay.
  useEffect(() => {
    if (!showDebug || !splatUrl || !scene) return
    let cancelled = false
    let group = null
    ;(async () => {
      const bust = splatVersion ? `?v=${encodeURIComponent(splatVersion)}` : ''
      const { url, res } = await fetchFirstOk(await collisionMeshCandidates(splatUrl), bust)
      const ct = res?.headers.get('content-type') || ''
      if (!res || !res.ok || /html/.test(ct)) {
        // No collision mesh baked. REPORTED, not silent, so the outliner's
        // COLLISION row can say so — the invisibility these rows exist to fix.
        if (typeof window !== 'undefined') window.__COLLISION__ = { state: 'no-cache', url }
        return
      }
      const buf = await res.arrayBuffer()
      if (cancelled) return
      new GLTFLoader().parse(buf, '', (gltf) => {
        if (cancelled) return
        group = new THREE.Group()
        // Collision mesh is baked in world space (Go navmesh bake) — no rotation.
        // (Legacy splat-transform meshes were engine-space and needed Rz180.)
        const st = navSettingsStore.getState().style
        gltf.scene.traverse((o) => {
          if (o.isMesh) {
            o.material = makeCollisionMaterial(st)
            o.renderOrder = st.collisionOnTop ? 9999 : 0
          }
        })
        group.renderOrder = st.collisionOnTop ? 9999 : 0
        group.add(gltf.scene)
        group.visible = navSettingsStore.getState().show.collision
        scene.add(group)
        collisionRef.current = group
        if (typeof window !== 'undefined') {
          window.__COLLISION_MESH__ = group
          window.__COLLISION__ = { state: 'applied', url }
        }
        console.log(`[NavMeshVolume] collision mesh applied from ${url}`)
      }, (err) => console.warn('[NavMeshVolume] collision mesh parse failed:', err))
    })().catch(err => console.warn('[NavMeshVolume] collision mesh load failed:', err))
    return () => {
      cancelled = true
      if (group) { scene.remove(group); group.traverse(o => { o.geometry?.dispose?.(); o.material?.dispose?.() }) }
      collisionRef.current = null
      if (typeof window !== 'undefined') { delete window.__COLLISION_MESH__; delete window.__COLLISION__ }
    }
  }, [splatUrl, splatVersion, showDebug, scene, reloadKey])

  // Live-toggle the collision mesh from the COLLISION row's own toggle.
  useEffect(() => {
    if (collisionRef.current) collisionRef.current.visible = show.collision
  }, [show.collision])

  // Live-apply the collision render style (shaded/wireframe, always-on-top)
  // from the COLLISION row's properties — rebuild the material in place, no GLB
  // reload.
  useEffect(() => {
    const g = collisionRef.current
    if (!g) return
    const order = style.collisionOnTop ? 9999 : 0
    g.renderOrder = order
    g.traverse((o) => {
      if (o.isMesh) {
        o.material?.dispose?.()
        o.material = makeCollisionMaterial(style)
        o.renderOrder = order
      }
    })
  }, [style.collisionMode, style.collisionOnTop])

  // Keep the movement component pointed at the live avatar group + drive ticks.
  useFrame((_, delta) => {
    if (!movement._group && typeof window !== 'undefined' && window.__AVATAR_GROUP__) {
      movement.setGroup(window.__AVATAR_GROUP__)
    }
    movement.tick(delta)
  })

  if (!overlay || !wantsOverlay) return null
  // The graph is drawn in the BAKE's space; the group lifts it to the world floor
  // (see NavDebugOverlay).
  return <NavDebugOverlay overlay={overlay} bakeFloorY={navRef.current?.floorY ?? 0} />
}

// Collision-mesh material from the panel STYLE row. 'shaded' = flat-shaded
// translucent faces (reads like the source guide — you see the near surface);
// 'wireframe' = orange edges. collisionOnTop disables depth so the splat can't
// occlude it (the old always-on-top tangle, now opt-in).
function makeCollisionMaterial(style) {
  const onTop = !!style?.collisionOnTop
  const common = { color: 0xff8030, transparent: true, depthTest: !onTop, depthWrite: !onTop }
  if (style?.collisionMode === 'wireframe') {
    return new THREE.MeshBasicMaterial({ ...common, wireframe: true, opacity: onTop ? 0.5 : 0.85 })
  }
  // Shaded: flat-shaded standard material so per-face lighting reveals form;
  // double-sided + translucent so you can see through it onto the splat.
  return new THREE.MeshStandardMaterial({
    ...common, opacity: 0.45, side: THREE.DoubleSide, flatShading: true,
    roughness: 0.9, metalness: 0.0, emissive: 0x4a2400, emissiveIntensity: 0.4,
  })
}

// Build line-segment buffers: navmesh edges (blue) + collision-box wireframes (orange).
function buildOverlay(nm) {
  const positions = []
  for (const n of nm.nodes) {
    for (const { to } of nm.adj[n.i]) {
      if (to <= n.i) continue // each undirected edge once
      const a = n.p, b = nm.nodes[to].p
      positions.push(a[0], a[1] + 0.02, a[2], b[0], b[1] + 0.02, b[2])
    }
  }
  const boxPositions = []
  const E = [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6], [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]]
  for (const box of nm.boxes || []) {
    // Oriented box: center + half-extents + yaw (about Y). Rotate the local
    // corner offsets so the wireframe follows the object's orientation.
    const [cx, cy, cz] = box.center, [hx, hy, hz] = box.half, yaw = box.yaw || 0
    const cos = Math.cos(yaw), sin = Math.sin(yaw)
    const corner = (sx, sy, sz) => {
      const lx = sx * hx, lz = sz * hz
      return [cx + lx * cos - lz * sin, cy + sy * hy, cz + lx * sin + lz * cos]
    }
    const c = [
      corner(-1, -1, -1), corner(1, -1, -1), corner(1, -1, 1), corner(-1, -1, 1),
      corner(-1, 1, -1), corner(1, 1, -1), corner(1, 1, 1), corner(-1, 1, 1),
    ]
    for (const [i, j] of E) boxPositions.push(...c[i], ...c[j])
  }
  return { positions: new Float32Array(positions), boxPositions: new Float32Array(boxPositions) }
}

function NavDebugOverlay({ overlay, bakeFloorY = 0 }) {
  const pathRef = useRef(null)
  // The navmesh graph's node heights are in the BAKE's space: Startup_Level bakes
  // at floorY -6.2 while the avatar stands at 0, so drawing the edges at their
  // own y put the whole grid 6.2 m under the floor — visible, far below, and
  // easy to read as "the overlay is broken". One offset on the group lifts every
  // edge AND the active-path line, and keeps whatever per-node height variation
  // the bake carries (rather than flattening the graph onto one plane).
  //
  // Subscribed, not read once: the world floor arrives asynchronously (the splat
  // reports its detected floor after load), so an offset captured at build time
  // would be stale exactly on the first frames someone is looking at.
  const [floorY, setFloorY] = useState(() => avatarFloor.getY())
  useEffect(() => avatarFloor.subscribe(y => setFloorY(y)), [])
  // Update the active-path polyline each frame from movement state.
  useFrame(() => {
    const line = pathRef.current
    if (!line) return
    const path = movement.path
    const geom = line.geometry
    if (!path || path.length < 2) {
      geom.setDrawRange(0, 0)
      return
    }
    const arr = geom.attributes.position.array
    const n = Math.min(path.length, arr.length / 3)
    for (let i = 0; i < n; i++) {
      arr[i * 3] = path[i][0]
      arr[i * 3 + 1] = path[i][1] + 0.05
      arr[i * 3 + 2] = path[i][2]
    }
    geom.attributes.position.needsUpdate = true
    geom.setDrawRange(0, n)
  })

  // Debug overlay must draw on top of the gaussian-splat environment, which
  // renders with its own depth/order. Force always-on-top: high renderOrder +
  // depthTest/depthWrite off so the lines aren't occluded by (or hidden behind)
  // the splat — otherwise they only peek through where the splat is sparse.
  return (
    <group renderOrder={9999} position={[0, floorY - bakeFloorY, 0]}>
      <lineSegments renderOrder={9999}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={overlay.positions.length / 3}
            array={overlay.positions}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={0x3399ff} transparent opacity={0.6} depthTest={false} depthWrite={false} />
      </lineSegments>
      {overlay.boxPositions && overlay.boxPositions.length > 0 && (
        <lineSegments renderOrder={9999}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={overlay.boxPositions.length / 3}
              array={overlay.boxPositions}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color={0xff8030} transparent opacity={0.95} depthTest={false} depthWrite={false} />
        </lineSegments>
      )}
      <line ref={pathRef} renderOrder={9999}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={1024}
            array={new Float32Array(1024 * 3)}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={0xffcc33} linewidth={2} depthTest={false} depthWrite={false} />
      </line>
    </group>
  )
}
